import { useQuery } from '@tanstack/react-query';
import Select from 'react-select';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { decodeHtmlEntities } from '../utils/decodeHtmlEntities';

interface CountrySelectionProps {
  onSelect: (countryCode: string, stateCode: string) => void;
}

interface Country {
  code: string;
  name: string;
  hidden?: boolean;
}

interface State {
  code: string;
  name: string;
}

interface Option {
  value: string;
  label: string;
}

export default function CountrySelection({ onSelect }: CountrySelectionProps) {
  const { t, i18n } = useTranslation();
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [hasNoStates, setHasNoStates] = useState<boolean>(false);

  const { data: countries, isLoading: isLoadingCountries, error: countriesError } = useQuery<Country[]>({
    queryKey: ['countries', i18n.language],
    queryFn: async () => {
      const response = await fetch(`/api/countries?lang=${i18n.language}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) {
        throw new Error('Error carregant països');
      }
      return response.json();
    },
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000
  });

  const { data: states, isLoading: isLoadingStates, isSuccess: isStatesSuccess } = useQuery<State[]>({
    queryKey: ['states', selectedCountry, i18n.language],
    queryFn: async () => {
      if (!selectedCountry) return [];

      const response = await fetch(`/api/states/${selectedCountry}?lang=${i18n.language}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) {
        throw new Error('Error carregant estats/províncies');
      }
      return response.json();
    },
    enabled: !!selectedCountry
  });

  // Detectar si el país no té estats/províncies
  useEffect(() => {
    if (isStatesSuccess && states && states.length === 0 && selectedCountry) {
      setHasNoStates(true);
    } else {
      setHasNoStates(false);
    }
  }, [states, isStatesSuccess, selectedCountry]);

  const handleCountryChange = (option: Option | null) => {
    if (option) {
      setSelectedCountry(option.value);
      // Guardem el país seleccionat per futurs accessos
      localStorage.setItem('selectedCountry', option.value);
    }
  };

  const handleStateChange = (option: Option | null) => {
    if (option && selectedCountry) {
      // Guardem l'estat/província seleccionada per futurs accessos
      localStorage.setItem('selectedState', option.value);
      // També guardem un codi postal provisional ("0") fins que l'usuari n'introdueixi un
      localStorage.setItem('tempPostcode', '0');
      
      onSelect(selectedCountry, option.value);
    }
  };

  const handleContinueWithoutState = () => {
    if (selectedCountry) {
      // Per països sense estats, utilitzem un codi especial "NA"
      localStorage.setItem('selectedState', 'NA');
      localStorage.setItem('tempPostcode', '0');
      
      onSelect(selectedCountry, 'NA');
    }
  };

  const isLoading = isLoadingCountries || isLoadingStates;

  if (isLoading && !selectedCountry) {
    return (
      <div className="py-8 text-center">
        <div className="text-xl">Carregant països...</div>
      </div>
    );
  }

  if (countriesError) {
    return (
      <div className="py-8 text-center">
        <div className="text-xl text-red-600">Error carregant països</div>
      </div>
    );
  }

  // Filtrar els països ocults
  const visibleCountries = countries?.filter(country => !country.hidden) || [];

  const countryOptions = visibleCountries
    .map(country => ({ value: country.code, label: decodeHtmlEntities(country.name) }))
    .sort((a, b) => {
      if (a.value === 'USE') return -1;
      if (b.value === 'USE') return 1;
      return 0;
    });

  const stateOptions = states?.map(state => ({
    value: state.code,
    label: decodeHtmlEntities(state.name)
  })) || [];

  return (
    <div className="bg-white shadow-md rounded-lg p-6">
      <div className="space-y-8">
        <div>
          <h2 className="text-center text-2xl font-bold text-gray-900">
            {t('countrySelection.title')}
          </h2>
        </div>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('country.country')}
            </label>
            <Select
              className="mt-1"
              classNamePrefix="select"
              defaultValue={null}
              isClearable={false}
              isSearchable={true}
              name="countrySelect"
              options={countryOptions}
              onChange={handleCountryChange}
              placeholder={t('country.searchCountry')}
              noOptionsMessage={() => t('country.noCountries')}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '48px',
                  borderRadius: '0.5rem',
                  borderColor: '#D1D5DB',
                  '&:hover': {
                    borderColor: '#2563EB'
                  }
                }),
                menu: (base) => ({
                  ...base,
                  borderRadius: '0.5rem',
                  marginTop: '0.5rem'
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused ? '#2563EB' : base.backgroundColor,
                  color: state.isFocused ? 'white' : base.color,
                  padding: '0.75rem 1rem'
                })
              }}
            />
          </div>
          
          {selectedCountry && !hasNoStates && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('country.state')}
              </label>
              {isLoadingStates ? (
                <div className="mt-2">{t('country.loadingStates')}</div>
              ) : (
                <Select
                  className="mt-1"
                  classNamePrefix="select"
                  defaultValue={null}
                  isClearable={false}
                  isSearchable={true}
                  name="stateSelect"
                  options={stateOptions}
                  onChange={handleStateChange}
                  placeholder={t('country.searchState')}
                  noOptionsMessage={() => t('country.noStates')}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '48px',
                      borderRadius: '0.5rem',
                      borderColor: '#D1D5DB',
                      '&:hover': {
                        borderColor: '#2563EB'
                      }
                    }),
                    menu: (base) => ({
                      ...base,
                      borderRadius: '0.5rem',
                      marginTop: '0.5rem'
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isFocused ? '#2563EB' : base.backgroundColor,
                      color: state.isFocused ? 'white' : base.color,
                      padding: '0.75rem 1rem'
                    })
                  }}
                />
              )}
            </div>
          )}
          
          {/* Mostrar el botó de continuar en aquests casos:
              1. Si el país té estats/províncies i l'usuari n'ha seleccionat un
              2. Si el país no té estats/províncies */}
          {selectedCountry && ((states && states.length > 0) || hasNoStates) && (
            <div className="pt-4">
              <button
                className="w-full py-2 px-4 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
                onClick={hasNoStates ? handleContinueWithoutState : () => document.querySelector('select[name="stateSelect"]')?.dispatchEvent(new Event('change'))}
              >
                {t('countrySelection.continue')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}